import { useEffect, useRef, useState } from 'react'
import { Box, Button, IconButton, Slider, Typography, ToggleButtonGroup, ToggleButton } from '@mui/material'
import { PlayArrow, Pause, SkipPrevious, SkipNext } from '@mui/icons-material'
import { format } from 'date-fns'
import { HeatmapFrame } from '@api/endpoints/marketing'

interface PlaybackControlsProps {
  frames: HeatmapFrame[]
  frameIndex: number
  onFrameIndexChange: (index: number) => void
}

// Base milliseconds per frame at 1x; higher speeds divide this.
const BASE_INTERVAL_MS = 900

function formatWindow(iso: string | null): string {
  if (!iso) return 'Aggregate (whole period)'
  try {
    return format(new Date(iso), 'MMM d, HH:mm')
  } catch {
    return iso
  }
}

/**
 * Play / pause / scrub through the per-window frames. Owns its own play state and
 * speed; the frame index is controlled by the parent so the map stays in sync.
 */
export function PlaybackControls({ frames, frameIndex, onFrameIndexChange }: PlaybackControlsProps) {
  const [isPlaying, setIsPlaying] = useState(false)
  const [speed, setSpeed] = useState(1)
  const frameIndexRef = useRef(frameIndex)
  frameIndexRef.current = frameIndex

  const frameCount = frames.length
  const canPlay = frameCount > 1

  // Stop playing if the dataset collapses to a single frame (e.g. aggregate mode).
  useEffect(() => {
    if (!canPlay && isPlaying) setIsPlaying(false)
  }, [canPlay, isPlaying])

  useEffect(() => {
    if (!isPlaying || !canPlay) return
    const interval = window.setInterval(() => {
      const next = (frameIndexRef.current + 1) % frameCount
      frameIndexRef.current = next
      onFrameIndexChange(next)
    }, BASE_INTERVAL_MS / speed)
    return () => window.clearInterval(interval)
  }, [isPlaying, canPlay, frameCount, speed, onFrameIndexChange])

  const step = (delta: number) => {
    if (frameCount === 0) return
    const next = (frameIndex + delta + frameCount) % frameCount
    onFrameIndexChange(next)
  }

  const currentWindow = frames[frameIndex]?.windowStart ?? null

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {/* One row at sm+: transport, slider, speed. On a phone the slider needs the full width to be
          scrubbable, so it drops to its own row under transport + speed (CSS order does the swap). */}
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 1, rowGap: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, order: 1 }}>
          <IconButton aria-label="Previous window" onClick={() => step(-1)} disabled={!canPlay}>
            <SkipPrevious />
          </IconButton>
          <Button
            variant="contained"
            size="small"
            onClick={() => setIsPlaying((p) => !p)}
            disabled={!canPlay}
            startIcon={isPlaying ? <Pause /> : <PlayArrow />}
            sx={{ minHeight: 36, minWidth: 88 }}
          >
            {isPlaying ? 'Pause' : 'Play'}
          </Button>
          <IconButton aria-label="Next window" onClick={() => step(1)} disabled={!canPlay}>
            <SkipNext />
          </IconButton>
        </Box>

        <Slider
          size="small"
          min={0}
          max={Math.max(0, frameCount - 1)}
          value={frameIndex}
          onChange={(_, v) => onFrameIndexChange(v as number)}
          disabled={!canPlay}
          aria-label="Time window"
          getAriaValueText={(v) => formatWindow(frames[v]?.windowStart ?? null)}
          sx={{
            order: { xs: 3, sm: 2 },
            flex: { xs: '1 1 100%', sm: '1 1 0' },
            minWidth: 0,
            mx: { xs: 1, sm: 2 },
          }}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, order: { xs: 2, sm: 3 }, ml: { xs: 'auto', sm: 0 } }}>
          <Typography variant="caption" color="text.secondary" id="playback-speed-label">
            Speed
          </Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={speed}
            onChange={(_, v) => v && setSpeed(v)}
            aria-labelledby="playback-speed-label"
          >
            <ToggleButton value={0.5}>0.5x</ToggleButton>
            <ToggleButton value={1}>1x</ToggleButton>
            <ToggleButton value={2}>2x</ToggleButton>
            <ToggleButton value={4}>4x</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
        {formatWindow(currentWindow)}
        {canPlay && ` — window ${frameIndex + 1} of ${frameCount}`}
      </Typography>
    </Box>
  )
}
